import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator, Pencil } from "lucide-react";

/**
 * GallonsField — a controlled gallons input with an optional rectangle-pool calculator.
 *
 * Props:
 *  - value (number)
 *  - onChange (fn, number) — called whenever the gallons value changes
 *  - testId (string) — for the visible gallons input
 *  - inputClassName (string) — applied to the gallons <Input/>
 *  - compact (bool) — hides the "Gallons" label
 */
/** Rectangle-pool calculator panel — computes gallons and hands the value back. */
const PoolCalculator = ({ testId, onApply, onClose }) => {
  const [dims, setDims] = useState({ length: "", width: "", shallow: "", deep: "" });
  const setDim = (k) => (e) => setDims({ ...dims, [k]: e.target.value });

  const compute = () => {
    const { length, width, shallow, deep } = dims;
    const L = parseFloat(length || 0);
    const W = parseFloat(width || 0);
    const S = parseFloat(shallow || 0);
    const D = parseFloat(deep || 0);
    if (L <= 0 || W <= 0 || S <= 0 || D <= 0) return;
    onApply(Math.round(L * W * ((S + D) / 2) * 7.48));
  };

  const fields = [
    ["length", "Length (ft)"],
    ["width", "Width (ft)"],
    ["shallow", "Shallow (ft)"],
    ["deep", "Deep (ft)"],
  ];

  return (
    <div className="border border-border bg-secondary/30 rounded-sm p-3 space-y-2" data-testid={`${testId}-calc`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calculator className="w-4 h-4 text-primary" />
          <Label className="text-xs font-semibold uppercase tracking-wider">Pool calculator (rectangle)</Label>
        </div>
        <Button type="button" size="sm" variant="ghost" onClick={onClose} className="rounded-sm h-7 px-2 text-xs" data-testid={`${testId}-close-calc`}>
          <Pencil className="w-3 h-3 mr-1" /> Type manually
        </Button>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {fields.map(([k, label]) => (
          <div key={k}>
            <Label className="text-[10px] text-muted-foreground">{label}</Label>
            <Input type="number" step="0.5" value={dims[k]} onChange={setDim(k)} className="rounded-sm mono-num h-9" data-testid={`${testId}-calc-${k}`} />
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between pt-1">
        <p className="text-[10px] text-muted-foreground font-mono">L × W × ((shallow+deep)/2) × 7.48</p>
        <Button type="button" size="sm" onClick={compute} className="rounded-sm h-8" data-testid={`${testId}-calc-apply`}>
          Calculate & apply
        </Button>
      </div>
    </div>
  );
};

export default function GallonsField({ value, onChange, testId, inputClassName = "rounded-sm mono-num", compact = false }) {
  const [mode, setMode] = useState("manual"); // "manual" | "calc"

  return (
    <div className="space-y-2">
      {mode === "manual" ? (
        <div className="flex items-center gap-2">
          {!compact && <Label className="text-xs">Gallons</Label>}
          <Input
            type="number"
            step="1"
            placeholder="Gallons"
            value={value || ""}
            onChange={(e) => onChange(parseFloat(e.target.value || 0))}
            className={inputClassName}
            data-testid={testId}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setMode("calc")}
            className="rounded-sm shrink-0"
            data-testid={`${testId || "gallons"}-open-calc`}
            title="Calculate from pool dimensions"
          >
            <Calculator className="w-3.5 h-3.5" />
          </Button>
        </div>
      ) : (
        <PoolCalculator
          testId={testId || "gallons"}
          onApply={(gallons) => { onChange(gallons); setMode("manual"); }}
          onClose={() => setMode("manual")}
        />
      )}
    </div>
  );
}
